"use client";

import { useState } from "react";
import { TEMPLATE_LIST } from "@/lib/carousel/templates";
import {
  NAME_FIELDS, ENGLISH_PRESET, RUSSIAN_PRESET, KAZAKH_PRESET, isNonEnglishOrder,
  type NameField, type NameOrder, type SpeciesNameSet,
} from "@/lib/carousel/names";

const SLOT_LABELS = ["Largest", "Medium", "Small"];

function ordersEqual(a: NameOrder, b: NameOrder): boolean {
  return a[0] === b[0] && a[1] === b[1] && a[2] === b[2];
}

/** Only these 4 are surfaced in the picker — the rest stay selectable by ID
 * (e.g. a trip that already used one keeps rendering fine) but aren't part
 * of the curated list shown here. */
const VISIBLE_TEMPLATE_IDS = ["field-diary", "glass-bar", "magazine", "minimal-light"];

export interface LeftPanelSlide {
  id: string;
  enabled: boolean;
  code?: string;
  names?: SpeciesNameSet;
  nameTertiary: string;
  namePrimary: string;
  nameSecondary: string;
  photoUrl: string | null;
}

export interface EditorLeftPanelProps {
  slides: LeftPanelSlide[];
  toggleSlide: (id: string) => void;
  setAllSlidesEnabled: (enabled: boolean) => void;
  reorderSlides: (fromId: string, toId: string) => void;
  templateId: string;
  setTemplateId: (id: string) => void;
  premiumLocked: boolean;
  onPremiumSwatchClick: () => void;
  nameOrder: NameOrder;
  onApplyNameOrder: (order: NameOrder) => void;
  /** Whether any imported slide actually has a Russian/Kazakh name — hides
   * the whole selector for public/guest imports that never carry regional
   * names, so it doesn't dangle as a no-op. */
  hasRegionalNames: boolean;
}

export default function EditorLeftPanel({
  slides, toggleSlide, setAllSlidesEnabled, reorderSlides,
  templateId, setTemplateId,
  premiumLocked, onPremiumSwatchClick,
  nameOrder, onApplyNameOrder, hasRegionalNames,
}: EditorLeftPanelProps) {
  const [search, setSearch] = useState("");
  const [draggedId, setDraggedId] = useState<string | null>(null);
  // Open the custom builder immediately if the active order doesn't match
  // any quick preset — otherwise a returning user with a custom combo would
  // see three unhighlighted preset pills and no obvious way to see what's
  // actually active.
  const [customizing, setCustomizing] = useState(
    !ordersEqual(nameOrder, ENGLISH_PRESET) && !ordersEqual(nameOrder, RUSSIAN_PRESET) && !ordersEqual(nameOrder, KAZAKH_PRESET)
  );
  // Collapsed behind a link for most visitors (browser locale decided
  // English) so it doesn't clutter the panel for people who'll never use
  // it — but starts open if a regional language is already active, whether
  // from locale detection or a remembered choice.
  const [revealNames, setRevealNames] = useState(isNonEnglishOrder(nameOrder));
  const visibleTemplates = TEMPLATE_LIST.filter((t) => VISIBLE_TEMPLATE_IDS.includes(t.id));
  const enabledCount = slides.filter((s) => s.enabled).length;
  const allEnabled = enabledCount === slides.length && slides.length > 0;

  const labelFor = (s: LeftPanelSlide) => s.names?.nameEn || s.namePrimary || s.nameTertiary || "Untitled";

  const q = search.trim().toLowerCase();
  const visible = q
    ? slides.filter((s) => labelFor(s).toLowerCase().includes(q))
    : slides;

  return (
    <div className="ced-left">
      <div className="ced-lp-sec ced-lp-sec--species">
        <div className="ced-lp-title">
          Species <span className="ced-lp-hint">{enabledCount}/{slides.length} selected</span>
        </div>
        <input
          type="text" className="car-headline-input ced-lp-search"
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="ced-lp-bulk-row">
          <button type="button" className="car-toggle-all" onClick={() => setAllSlidesEnabled(!allEnabled)}>
            {allEnabled ? "Select none" : "Select all"}
          </button>
        </div>
        <div className="ced-species-list">
          {visible.map((s) => (
            <div
              key={s.id}
              className={`car-bird-row${s.enabled ? " car-bird-row--on" : ""}${draggedId === s.id ? " car-bird-row--dragging" : ""}`}
              draggable
              onDragStart={(e) => { setDraggedId(s.id); e.dataTransfer.effectAllowed = "move"; }}
              onDragEnd={() => setDraggedId(null)}
              onDragOver={(e) => { if (draggedId && draggedId !== s.id) e.preventDefault(); }}
              onDrop={(e) => {
                e.preventDefault();
                if (draggedId && draggedId !== s.id) reorderSlides(draggedId, s.id);
                setDraggedId(null);
              }}
            >
              <div className="car-bird-row-main">
                <span className="car-bird-drag-handle" aria-hidden="true">⠿</span>
                <input
                  type="checkbox" checked={s.enabled}
                  onChange={() => toggleSlide(s.id)}
                  className="car-checkbox"
                  aria-label="Include slide"
                />
                <button type="button" className="car-bird-row-names" onClick={() => toggleSlide(s.id)}>
                  <span className="car-species-name-en">{labelFor(s)}</span>
                </button>
              </div>
            </div>
          ))}
          {visible.length === 0 && <p className="car-birds-empty">No matches.</p>}
        </div>
      </div>

      <div className="ced-lp-sec ced-lp-sec--design">
        <div className="ced-lp-title">Template</div>
        <div className="car-picker-row">
          {visibleTemplates.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`car-pill${t.id === templateId ? " car-pill-on" : ""}`}
              onClick={() => {
                if (t.premium && premiumLocked) { onPremiumSwatchClick(); return; }
                setTemplateId(t.id);
              }}
            >
              {t.name}{t.premium && <span className="car-pill-premium">★</span>}
            </button>
          ))}
        </div>

        <div className="ced-lp-title">Names</div>
        {!hasRegionalNames ? (
          <label className="ced-lp-checkbox">
            <input
              type="checkbox" checked={nameOrder[1] === "scientific"}
              onChange={(e) => onApplyNameOrder(e.target.checked ? ["en", "scientific", null] : ["en", null, null])}
            />
            Show scientific name
          </label>
        ) : revealNames ? (
          <>
            <div className="car-picker-row">
              <button type="button" className={`car-pill${ordersEqual(nameOrder, ENGLISH_PRESET) ? " car-pill-on" : ""}`} onClick={() => onApplyNameOrder(ENGLISH_PRESET)}>English</button>
              <button type="button" className={`car-pill${ordersEqual(nameOrder, RUSSIAN_PRESET) ? " car-pill-on" : ""}`} onClick={() => onApplyNameOrder(RUSSIAN_PRESET)}>Russian</button>
              <button type="button" className={`car-pill${ordersEqual(nameOrder, KAZAKH_PRESET) ? " car-pill-on" : ""}`} onClick={() => onApplyNameOrder(KAZAKH_PRESET)}>Kazakh</button>
            </div>

            {customizing ? (
              <div className="ced-lp-name-order">
                {SLOT_LABELS.map((label, i) => (
                  <div key={label} className="ced-lp-name-order-row">
                    <span>{label}</span>
                    <select
                      value={nameOrder[i] ?? ""}
                      onChange={(e) => {
                        const next: NameOrder = [...nameOrder];
                        next[i] = (e.target.value || null) as NameField | null;
                        onApplyNameOrder(next);
                      }}
                    >
                      <option value="">— None —</option>
                      {NAME_FIELDS.map((f) => (
                        <option key={f.id} value={f.id} disabled={nameOrder.some((used, j) => used === f.id && j !== i)}>
                          {f.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            ) : (
              <button type="button" className="car-toggle-all" onClick={() => setCustomizing(true)}>
                Customize order →
              </button>
            )}

            <button type="button" className="car-toggle-all" onClick={() => { setRevealNames(false); setCustomizing(false); }}>
              Hide name languages
            </button>
          </>
        ) : (
          <button type="button" className="car-toggle-all" onClick={() => setRevealNames(true)}>
            More name languages
          </button>
        )}
      </div>
    </div>
  );
}
